import React, { useEffect, useRef, memo } from 'react';

interface TradingViewWidgetProps {
  symbol: string;
  theme?: "light" | "dark";
  range?: string;
  width?: string;
  height?: string;
}

function TradingViewWidget({ 
  symbol, 
  theme = "light",
  range = "3M", 
  width = "100%",
  height = "calc(100% - 32px)"
}: TradingViewWidgetProps) {
  const container = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);

  useEffect(() => {
    if (!container.current) return;
    
    // Clean up any previous scripts
    const existingScript = container.current.querySelector('script');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type = "text/javascript";
    script.async = true;
    
    // Format the symbol correctly for TradingView
    const formattedSymbol = symbol; // Removed NASDAQ default
    
    script.innerHTML = JSON.stringify({
      "autosize": true,
      "symbol": formattedSymbol,
      "timezone": "Etc/UTC",
      "theme": theme,
      "style": "8", // Candlestick style
      "locale": "en",
      "backgroundColor": "rgba(242, 242, 242, 1)",
      "gridColor": "rgba(182, 182, 182, 0.06)",
      "withdateranges": true,
      "range": range,
      "hide_side_toolbar": false,
      "allow_symbol_change": false,
      "details": true,
      "hotlist": true,
      "studies": [
        "STD;VWMA"
      ],
      "support_host": "https://www.tradingview.com"
    });

    container.current.appendChild(script);
    widgetRef.current = script;
    
    return () => {
      if (container.current && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [symbol, theme, range]);

  return (
    <div className="tradingview-widget-container" ref={container} style={{ height: "100%" }}>
      <div className="tradingview-widget-container__widget" style={{ height, width }}></div>
      <div className="tradingview-widget-copyright">
        <a 
          href="https://www.tradingview.com/" 
          rel="noopener nofollow" 
          target="_blank"
        >
          <span className="blue-text">Track all markets on TradingView</span>
        </a>
      </div>
    </div>
  );
}

export default memo(TradingViewWidget);